import React, {useCallback, useRef} from 'react';
import {PROMPT_TEXTAREA_MAX_HEIGHT_PX} from '../AIMessageGenerator.css';
import {logGoogleEvent} from '../../../utils/analytics';
import resizeTextareaToContent from '../../../library/resizeTextareaToContent';
import {canSubmitPrompt} from './attachmentHelpers';
import type {PendingChatAttachment, SubmitPromptOptions} from './types';

interface UseGeneratorPromptArgs {
    promptText: string;
    setPromptText: (text: string) => void;
    draftLoading: boolean;
    hasPendingAttachment: boolean;
    submitPrompt: (
        promptOverride?: string,
        attachment?: PendingChatAttachment | null,
        displayText?: string,
    ) => Promise<void>;
    analytics?: {
        contentId: string | number;
        sourceTypeId: string;
        context?: Record<string, unknown>;
    };
}

/** Textarea sizing, keyboard handling and analytics around the prompt box. */
export function useGeneratorPrompt({
    promptText,
    setPromptText,
    draftLoading,
    hasPendingAttachment,
    submitPrompt,
    analytics,
}: UseGeneratorPromptArgs) {
    const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollColumnRef = useRef<HTMLDivElement>(null);

    const logAnalytics = useCallback((eventName: string, extra?: Record<string, unknown>) => {
        if(!analytics) {
            return;
        }
        logGoogleEvent(eventName, analytics.contentId, analytics.sourceTypeId, {
            popup_name: 'aiMessageGenerator',
            ...(analytics.context ?? {}),
            ...(extra ?? {}),
        });
    }, [analytics]);

    // Scrolling lives in the panel — it reacts to every message, not just this one.
    const handleSubmitPrompt = useCallback(async ({
        text: promptOverride,
        displayText,
        attachment,
    }: SubmitPromptOptions = {}) => {
        const text = (promptOverride ?? promptText).trim();
        if(!canSubmitPrompt(text, attachment) || draftLoading) {
            return;
        }
        logAnalytics('ai_message_generator_submit_click', {button_name: 'submit'});
        await submitPrompt(text, attachment, displayText);
    }, [promptText, draftLoading, submitPrompt, logAnalytics]);

    // `text` carries the full server prompt; the bubble keeps the short chip wording.
    const handleSuggestionClick = useCallback((text: string, index: number, displayText?: string) => {
        logAnalytics('ai_message_generator_suggestion_click', {
            suggestion_index: index,
            button_name: `suggestion_${index}`,
        });
        setPromptText(displayText ?? text);
        void handleSubmitPrompt({text, displayText});
    }, [logAnalytics, setPromptText, handleSubmitPrompt]);

    // Same chips, but the prompt lands in the box and waits — nothing is sent until the user says so.
    const handleSuggestionFill = useCallback((text: string, index: number) => {
        logAnalytics('ai_message_generator_suggestion_fill', {
            suggestion_index: index,
            button_name: `suggestion_${index}`,
        });
        setPromptText(text);
        const textarea = promptTextareaRef.current;
        if(!textarea) {
            return;
        }
        textarea.focus({preventScroll: true});
        // The box still holds the previous value this tick, so size it once the text is in.
        requestAnimationFrame(() => {
            resizeTextareaToContent(textarea, PROMPT_TEXTAREA_MAX_HEIGHT_PX);
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        });
    }, [logAnalytics, setPromptText]);

    const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizeTextareaToContent(e.target, PROMPT_TEXTAREA_MAX_HEIGHT_PX);
        setPromptText(e.target.value);
    }, [setPromptText]);

    const handlePromptKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(e.key !== 'Enter' || e.shiftKey) {
            return;
        }
        e.preventDefault();
        if((!promptText.trim() && !hasPendingAttachment) || draftLoading) {
            return;
        }
        void handleSubmitPrompt();
    }, [promptText, hasPendingAttachment, draftLoading, handleSubmitPrompt]);

    return {
        promptTextareaRef,
        scrollColumnRef,
        logAnalytics,
        handleSubmitPrompt,
        handleSuggestionClick,
        handleSuggestionFill,
        handlePromptChange,
        handlePromptKeyDown,
    };
}
